import { useMemo } from 'react';
import SkeletonView, { jointOf, rootOf } from '../skeleton/SkeletonView';
import { frameToBodies } from '../../SkeletonStickFigure';
import { Button, Segmented, Slider } from '../ui';
import { fmtClock, toMs } from '../../seriesExtractors';

const TRAIL_S = 5;
const SPEEDS = [0.5, 1, 2, 4].map((v) => ({ value: v, label: `${v}×` }));
const MODES = [{ value: 'pose', label: 'Pose' }, { value: 'trajectory', label: 'Trajectory' }];

/** Index of the last element ≤ x in a sorted array (-1 when none). */
function floorIndex(arr, x) {
    let lo = 0;
    let hi = arr.length - 1;
    let ans = -1;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (arr[mid] <= x) { ans = mid; lo = mid + 1; } else hi = mid - 1;
    }
    return ans;
}

/**
 * Replay — recorded skeleton at the playhead (pose) or the right-hand path over the last 5 s
 * (trajectory), with transport controls. Time is seconds on the shared timeline; the chart's
 * playhead and click-to-seek use the same clock.
 */
export default function ReplayPanel({
    frames, skelStatus, t0, duration, t, playing, speed, mode, onMode, onSeek, onNudge, onTogglePlay, onSpeed,
    events, lastEventNote, sticky, height,
}) {
    // Frame times (s on the timeline) + per-frame first body + right-hand track, computed once.
    const track = useMemo(() => {
        const times = [];
        const bodies = [];
        const hand = [];
        (frames || []).forEach((f) => {
            const ts = toMs(f?.timestamp);
            if (ts == null) return;
            const b = frameToBodies(f).filter((x) => x.joints && Object.keys(x.joints).length);
            times.push((ts - t0) / 1000);
            bodies.push(b);
            hand.push(b[0] ? jointOf(b[0].joints, 'HandRight') : null);
        });
        const first = bodies.find((b) => b.length);
        const root = first ? rootOf(first[0].joints) : null;
        return { times, bodies, hand, origin: root ? [root[0], root[2]] : undefined };
    }, [frames, t0]);

    const idx = track.times.length ? Math.max(0, floorIndex(track.times, t)) : -1;
    const shown = useMemo(() => (idx >= 0 ? track.bodies[idx].map((b, i) => ({ id: b.id ?? i, joints: b.joints })) : []), [track, idx]);
    const trails = useMemo(() => {
        if (mode !== 'trajectory' || idx < 0) return [];
        const pts = [];
        const from = t - TRAIL_S;
        for (let i = idx; i >= 0 && track.times[i] >= from; i--) if (track.hand[i]) pts.push(track.hand[i]);
        return pts.length > 1 ? [{ points: pts.reverse() }] : [];
    }, [mode, idx, t, track]);

    const emptyText = skelStatus === 'failed'
        ? 'Skeleton data didn’t load'
        : skelStatus === 'loading'
            ? 'Loading skeleton…'
            : !track.times.length ? 'No skeleton recorded in this session' : null;

    // Event ticks on the scrubber — skipped when there are too many to tell apart.
    const ticks = (events || []).length > 80 ? [] : (events || []).map((e) => ({ value: Math.max(0, Math.min(duration, e.t)), label: `${e.typeLabel ? e.typeLabel + ' · ' : ''}${e.label} · ${fmtClock(e.t)}`, color: e.color }));
    const last = (events || []).filter((e) => e.t <= t + 1e-6).pop();

    const onKeyCapture = (e) => {
        if (e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
            e.stopPropagation();
            onTogglePlay();
        } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            e.stopPropagation();
            onNudge(e.key === 'ArrowRight' ? 1 : -1);
        }
    };

    return (
        <section aria-label="Replay" className={'sd-replay' + (sticky ? ' sd-replay--sticky' : '')}>
            <div className="sd-replay__head">
                <span className="sd-replay__title">Replay</span>
                <Segmented options={MODES} value={mode} onChange={onMode} label="Viewer mode" size="sm" />
            </div>
            <SkeletonView
                bodies={emptyText ? [] : shown}
                trails={emptyText ? [] : trails}
                mode={mode}
                height={height}
                origin={track.origin}
                emptyText={emptyText || 'No skeleton at this moment'}
            />
            <div className="sd-replay__ctl">
                <div onKeyDownCapture={onKeyCapture}>
                    <Slider value={Math.min(t, duration)} min={0} max={duration} step={0.1} onChange={onSeek} label="Replay position" ticks={ticks} />
                </div>
                <div className="sd-transport">
                    <Button variant="icon" icon="back5" iconSize={22} aria-label="Back 5 seconds" onClick={() => onNudge(-5)} />
                    <Button variant="primary" className="sd-play" icon={playing ? 'pause' : 'play'} iconSize={22} aria-label={playing ? 'Pause replay' : 'Play replay'} onClick={onTogglePlay} />
                    <Button variant="icon" icon="fwd5" iconSize={22} aria-label="Forward 5 seconds" onClick={() => onNudge(5)} />
                    <span className="sd-time" aria-live="off">
                        {fmtClock(t)} <span className="sd-time__total">/ {fmtClock(duration)}</span>
                    </span>
                    <Segmented options={SPEEDS} value={speed} onChange={onSpeed} label="Playback speed" plain />
                </div>
                <div className="sd-last">
                    <span className="eg-caps-14 eg-secondary eg-nowrap">Last event</span>
                    <span className="sd-last__text">{last ? `${last.label} · ${fmtClock(last.t)}` : lastEventNote}</span>
                </div>
            </div>
        </section>
    );
}
